import React, { useState, useEffect } from 'react';
import { side, sideMenuTypes } from 'config/Routes';
import Icon from 'components/commons/icon/Icon';

// レコード数にあわせて、アニメーション可動域を算出
const calcMaxHeight = (
  openSideSubMenu: boolean,
  length: number | undefined
): string => {
  const pxZero = '0px';
  if (!openSideSubMenu || length === undefined) return pxZero;

  const itemHeight =
    document.getElementById('id_sideMenuItem')?.offsetHeight || 0;
  return `${itemHeight * length}px`;
};

type Props = {
  hideAnimate: boolean;
  menuHide: () => void;
  onNavLinkClick(e: React.MouseEvent, path: string): void;
};

const LoginSideMenu: React.FC<Props> = props => {
  const { hideAnimate, menuHide } = props;
  const [isOpen, setIsOpen] = useState(true);
  const [isOpenMenuBeginner, setOpenMenuBeginner] = useState(false);

  const openCloseMenuBeginner = (): void => {
    setOpenMenuBeginner(!isOpenMenuBeginner);
  };

  useEffect(() => {
    if (hideAnimate) setIsOpen(false);
  }, [hideAnimate]);

  // 閉じる要求が来た際に、アニメーションを動かす
  const onCloseRequest = (): void => {
    setIsOpen(false);
  };

  // アニメーションの終わりを待って、コンポーネントを非表示にしてもらう
  // (厳密には、コンテンツ(サブメニュー)とマスクの両方の終わりを待つべきだが、同じ時間で動かすようにしているので、コンテンツの方だけで見る)
  const onAnimated = (): void => {
    if (!isOpen) menuHide();
  };

  const animationOption =
    ' animation-200ms animation-ease-in-out animation-fill-forwards';

  const showOrHideAnimation = isOpen
    ? { content: 'animation-appear-from-right', mask: 'animation-fadeIn' }
    : { content: 'animation-disappear-to-right', mask: 'animation-fadeOut' };
  showOrHideAnimation.content += animationOption;
  showOrHideAnimation.mask += animationOption;

  const offsetTop = document.getElementById('id_header')?.offsetHeight ?? 0;

  const chevronRight = (
    <Icon name="chevron-right" className="text-2xl text-gray-500" />
  );

  const sideMenuBaseClass = 'flex items-center py-2 pl-6 pr-3';
  const sideMenuClass = `${sideMenuBaseClass} text-white text-sm cursor-pointer`;
  const sideMenuFinance = (
    <div>
      <div className={`${sideMenuBaseClass} text-gray-500 text-sm`}>
        金融商品メディア
      </div>
      <div className="border-b border-gray-700" />
      {side
        .filter(
          item =>
            item?.menuType && item?.menuType === sideMenuTypes.FinancialProduct
        )
        .map((item, i) => {
          return (
            <div key={i}>
              <a
                id={item.id}
                href={item.path}
                rel="noopener noreferrer"
                className={sideMenuClass}
                onClick={(): void => onCloseRequest()}>
                <div className="flex-grow">{item.title}</div>
                {chevronRight}
              </a>
              <div className="border-b border-gray-700" />
            </div>
          );
        })}
    </div>
  );

  const sideMenuBeginner = side
    .filter(item => {
      return item?.menuType === sideMenuTypes.Beginner;
    })
    .map((item, i) => {
      // max-h-custom1・max-h-0の指定を止めて、maxHeightを算出
      const maxHeight = calcMaxHeight(
        isOpenMenuBeginner,
        item?.subMenuList ? item.subMenuList.length : undefined
      );
      return (
        <div key={i}>
          <div className={sideMenuClass} onClick={openCloseMenuBeginner}>
            <div className="flex-grow">{item.title}</div>
            <Icon
              name={`chevron-down transform rotate-${
                isOpenMenuBeginner ? '180' : '0'
              }`}
              className="text-2xl text-gray-500 transition duration-300"
              caption="開閉"
            />
          </div>
          <div className="border-b border-gray-700" />
          <div
            className="overflow-hidden transition-all duration-500"
            style={{ maxHeight: `${maxHeight}` }}>
            {item?.subMenuList &&
              item.subMenuList.map((subItem, ii) => {
                return (
                  <div
                    key={subItem.path}
                    id="id_sideMenuItem"
                    onClick={(): void => onCloseRequest()}>
                    <a
                      id={subItem.id}
                      href={subItem.path}
                      rel="noopener noreferrer"
                      className={sideMenuClass}
                      style={{
                        backgroundColor: '#20293b',
                      }}>
                      <div className="flex-grow pl-4">{subItem.title}</div>
                      {chevronRight}
                    </a>
                    <div className="border-b border-gray-700" />
                  </div>
                );
              })}
          </div>
        </div>
      );
    });

  const sideMenuNomalClass = `${sideMenuBaseClass} text-white text-xs`;
  const sideMenuNomal = (
    <div className="mt-3">
      {side
        .filter(
          item => item?.menuType && item?.menuType === sideMenuTypes.Nomal
        )
        .map((item, i) => {
          return (
            <a
              id={item.id}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              key={item.path}
              className={sideMenuNomalClass}
              onClick={(): void => onCloseRequest()}>
              {item.title}
            </a>
          );
        })}
    </div>
  );

  return (
    <div
      className="overflow-y-auto relative h-full w-full"
      style={{
        zIndex: 100,
      }}>
      <div
        className={`fixed left-0 bg-gray-800 h-full w-full ${showOrHideAnimation.mask}`}
        onClick={onCloseRequest}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      />
      <div
        className={`fixed right-0 bg-gray-700 h-full overflow-y-auto w-64 ${showOrHideAnimation.content}`}
        style={{
          backgroundColor: '#374151',
          width: '18rem',
          height: `calc(var(--vh, 1vh) * 100 - ${offsetTop}px)`,
        }}
        onAnimationEnd={onAnimated}>
        <div className="p-4 text-center">
          <ul
            id="family-link"
            className="rounded-lg border border-bluegray-600 grid grid-cols-4 text-xs text-white">
            <li className="block pt-2 pb-1 border-bluegray-600 border-b border-r">
              <a
                id="side_stock_fromIcon"
                href="https://minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_kabu.svg"
                  width="30px"
                  height="30px"
                  alt="株式"
                  className="m-auto"
                />
                <p className="mt-2">株式</p>
              </a>
            </li>
            <li className="block pt-2 pb-1 border-bluegray-600 border-b border-r">
              <a
                id="side_us_stock_fromIcon"
                href="https://us.minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_us.svg"
                  width="30px"
                  height="30px"
                  alt="米国株"
                  className="m-auto"
                />
                <p className="mt-2">米国株</p>
              </a>
              <div className="ml-10 -mt-14">
                <img src="/static/images/new.svg" alt="new" />
              </div>
            </li>
            <li className="block pt-2 pb-1 border-bluegray-600 border-b border-r">
              <a
                id="side_investmentTrust_fromIcon"
                href="https://itf.minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_toushin.svg"
                  width="30px"
                  height="30px"
                  alt="投資信託"
                  className="m-auto"
                />
                <p className="mt-2">投資信託</p>
              </a>
            </li>
            <li className="block pt-2 pb-1 border-bluegray-600 border-b">
              <a
                id="side_insurance"
                href="https://ins.minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_hoken.svg"
                  width="30px"
                  height="30px"
                  alt="保険"
                  className="m-auto"
                />
                <p className="mt-2">保険</p>
              </a>
            </li>
            <li className="block pt-2 pb-1 border-bluegray-600 border-r">
              <a
                id="side_FX_fromIcon"
                href="https://fx.minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_fx.svg"
                  width="30px"
                  height="30px"
                  alt="FX/為替"
                  className="m-auto"
                />
                <p className="mt-2">FX/為替</p>
              </a>
            </li>
            <li className="block pt-2 pb-1 border-bluegray-600 border-r">
              <a
                id="side_futures_fromIcon"
                href="https://fu.minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_sakimono.svg"
                  width="30px"
                  height="30px"
                  alt="先物"
                  className="m-auto"
                />
                <p className="mt-2">先物</p>
              </a>
            </li>
            <li className="block pt-2 pb-1 border-bluegray-600 border-r">
              <a
                id="side_virtualCurrency_fromIcon"
                href="https://cc.minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_kasoutsuka.svg"
                  width="30px"
                  height="30px"
                  alt="仮想通貨"
                  className="m-auto"
                />
                <p className="mt-2">仮想通貨</p>
              </a>
            </li>
            <li className="block pt-2 pb-1 border-bluegray-600">
              <a
                id="side_studyingMoney_fromIcon"
                href="https://mag.minkabu.jp/"
                className="block"
                onClick={() => onCloseRequest()}>
                <img
                  src="https://assets.minkabu.jp/images/icon/icon_study.svg"
                  width="30px"
                  height="30px"
                  alt="お金の勉強"
                  className="m-auto"
                />
                <p className="mt-2">お金の勉強</p>
              </a>
            </li>
          </ul>
        </div>
        {sideMenuFinance}
        {sideMenuBeginner}
        {sideMenuNomal}
        <div className="h-24"></div>
      </div>
    </div>
  );
};

export default LoginSideMenu;
